import ReactSelect, { components, type InputProps, type StylesConfig } from 'react-select'

// Firefox can offer stored form history despite autocomplete="off". Use the
// new-password token on the actual search input to suppress that history
// and keep password managers out of these application search controls.
function SelectInput(props: InputProps<SelectOption, false>) {
  return <components.Input {...props} autoComplete="new-password" data-1p-ignore="true" data-lpignore="true" />
}

export interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  inputId?: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  'aria-label'?: string
  isDisabled?: boolean
  /** Matches the row-height selects use inline in tables (spec §3.2 Users). */
  compact?: boolean
}

const styles = (compact: boolean): StylesConfig<SelectOption, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: compact ? 32 : 36,
    background: 'var(--color-surface)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: state.isFocused
      ? 'var(--color-accent)'
      : 'var(--color-divider)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'none',
    cursor: 'pointer',
    ':hover': {
      borderColor: state.isFocused
        ? 'var(--color-accent)'
        : 'color-mix(in srgb, var(--color-text) 45%, transparent)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: compact ? '0 8px' : '2px 10px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'color-mix(in srgb, var(--color-text) 65%, transparent)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: compact ? 4 : 8,
    color: 'color-mix(in srgb, var(--color-text) 60%, transparent)',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
    ':hover': { color: 'var(--color-text)' },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: compact ? 4 : 8,
    color: 'color-mix(in srgb, var(--color-text) 60%, transparent)',
    ':hover': { color: 'var(--color-text)' },
  }),
  menu: (base) => ({
    ...base,
    background: 'var(--color-surface)',
    border: '1px solid var(--color-divider)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    overflow: 'hidden',
  }),
  // Portalled to <body>, so it needs to clear the sticky nav (z-index 20) —
  // `menuPlacement: 'auto'` can flip a menu up into it (issue #4).
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  menuList: (base) => ({
    ...base,
    padding: 4,
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    background: state.isSelected
      ? 'var(--color-accent)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--color-text) 7%, transparent)'
        : 'transparent',
    color: state.isSelected ? 'var(--color-bg)' : 'var(--color-text)',
  }),
})

/**
 * The design system's `.input` chrome only covers text fields — Broadsheet's
 * native `<select>` keeps the browser's own popover, which can't take the
 * theme. This wraps react-select instead so every dropdown (search, filters,
 * row-inline role pickers) matches the rest of the form chrome and gets
 * type-ahead filtering for free.
 *
 * The menu renders in a portal on `document.body` rather than inline (issue
 * #4). Inline, the row-inline role pickers open *inside* `.table-scroll`,
 * whose `overflow-x: auto` computes `overflow-y` to `auto` as well — so the
 * menu was clipped and grew the table's scroll area instead of floating over
 * it. The portal keeps the default `menuPosition: 'absolute'`, which offsets
 * by the page scroll, so the menu stays anchored to its control as the page
 * scrolls; `menuPlacement: 'auto'` flips it above the control near the foot of
 * the viewport.
 */
export function Select({
  inputId,
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
  compact = false,
  ...rest
}: SelectProps) {
  const selected = options.find((option) => option.value === value) ?? null

  return (
    <ReactSelect<SelectOption, false>
      inputId={inputId}
      components={{ Input: SelectInput }}
      unstyled
      value={selected}
      onChange={(option) => onChange(option?.value ?? '')}
      options={options}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={false}
      menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
      menuPlacement="auto"
      styles={styles(compact)}
      {...rest}
    />
  )
}
